/**
 * Model Releases — chronological timeline of frontier-model launches.
 * Curated list, newest first, grouped by year.
 */
import { useEffect, useMemo, useState } from 'react'
import { Rocket, RefreshCw, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

interface Release {
  date: string
  lab: string
  model: string
  tags: string[]
  highlight: string
}

const TAG_TONE: Record<string, string> = {
  frontier:      'bg-rose/15 text-rose-light',
  multimodal:    'bg-purple/15 text-purple-light',
  reasoning:     'bg-amber/15 text-amber-light',
  agents:        'bg-blue/15 text-blue-light',
  'open-weights':'bg-emerald/15 text-emerald-light',
}

export default function ModelReleasesPage() {
  const [releases, setReleases] = useState<Release[]>([])
  const [labs, setLabs] = useState<string[]>([])
  const [labFilter, setLabFilter] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/releases')
      .then(r => { setReleases(r.data?.releases || []); setLabs(r.data?.labs || []) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(
    () => labFilter ? releases.filter(r => r.lab === labFilter) : releases,
    [releases, labFilter],
  )
  const byYear = useMemo(() => {
    const groups: Record<string, Release[]> = {}
    for (const r of filtered) {
      const y = r.date.slice(0, 4)
      if (!groups[y]) groups[y] = []
      groups[y].push(r)
    }
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a))
  }, [filtered])

  // Per-year release count, oldest → newest, fills gaps with 0
  const releasesPerYear = useMemo(() => {
    if (releases.length === 0) return []
    const years = releases.map(r => parseInt(r.date.slice(0, 4), 10)).filter(y => y > 0)
    const minY = Math.min(...years)
    const maxY = Math.max(...years)
    const out: { t: string; v: number }[] = []
    for (let y = minY; y <= maxY; y++) {
      out.push({ t: String(y), v: releases.filter(r => parseInt(r.date.slice(0, 4), 10) === y).length })
    }
    return out
  }, [releases])

  const labCount = useMemo(() => new Set(releases.map(r => r.lab)).size, [releases])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Rocket size={18} className="text-amber-light" /> FRONTIER MODEL RELEASES
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          chronological · what changed · curated
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="card p-3">
          <div className="flex items-baseline gap-2">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Total releases</div>
            {releasesPerYear.length >= 2 && (
              <Sparkline points={releasesPerYear} tone="amber" width={70} height={16} className="ml-auto" />
            )}
          </div>
          <div className="text-2xl font-black font-mono mt-1 text-amber-light">{releases.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Labs tracked</div>
          <div className="text-2xl font-black font-mono mt-1 text-blue-light">{labCount}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Span</div>
          <div className="text-2xl font-black font-mono mt-1 text-emerald">
            {releasesPerYear.length > 0
              ? `${releasesPerYear[0].t}–${releasesPerYear[releasesPerYear.length - 1].t}`
              : '—'}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={labFilter} onChange={e => setLabFilter(e.target.value)} aria-label="Filter by lab">
          <option value="">All labs</option>
          {labs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <span className="text-text-muted ml-auto">{filtered.length} of {releases.length} releases</span>
      </div>

      {byYear.map(([year, items]) => (
        <section key={year}>
          <h2 className="text-[10px] uppercase tracking-[0.3em] text-text-muted mb-2 sticky top-0 bg-base py-1 z-10">
            {year}
          </h2>
          <ol className="relative border-l border-border ml-3 space-y-3 pl-5">
            {items.map(r => (
              <li key={r.date + r.lab + r.model} className="relative">
                <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-amber border-2 border-base" />
                <div className="card p-3">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[11px] font-mono text-amber-light font-semibold">{r.date}</span>
                    <span className="text-[10px] text-text-muted">{r.lab}</span>
                    <span className="text-sm font-bold text-text-primary ml-auto md:ml-0">{r.model}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {r.tags.map(t => (
                      <span key={t} className={clsx('badge text-[9px]', TAG_TONE[t] || 'bg-surface-raised text-text-muted')}>{t}</span>
                    ))}
                  </div>
                  <p className="text-xs text-text-secondary mt-2 leading-relaxed">{r.highlight}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}
